import {
  DataObjectOutputType,
  DataObjectRelationEdgeType,
  DataObjectRelationNodeType,
  DataObjectRelationsGraphDto,
  DataObjectVersionType,
  DataObjectType,
  getLatestDataObjectVersion,
} from '@atlas-frontend/sdk';
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  useReactFlow,
  Controls,
} from '@xyflow/react';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  RelationNodeType,
  RelationNode,
  RelationEdgeType,
  NodeData,
} from './RelationNode';
import styled from 'styled-components';
import {
  getEdgeStyle,
  getGroupNodeActiveVersion,
  getLayoutedElements,
} from './utils';
import { useSearchParams } from 'react-router-dom';
import { InputDataContextMenu } from './InputDataContextMenu';
import { OutputDataContextMenu } from './OutputDataContextMenu';

const nodeTypes = {
  relationNode: RelationNode,
};

type RelationsGraphProps = {
  graphData: DataObjectRelationsGraphDto;
  refetchGraphData: () => void;
};

type ContextMenuState = {
  top: number;
  left: number;
  nodeId: string;
  type: DataObjectType;
};
const RelationsGraphInner = ({
  graphData,
  refetchGraphData,
}: RelationsGraphProps) => {
  const { fitView } = useReactFlow();
  const [searchParams] = useSearchParams();

  const initialNodes: RelationNodeType[] = useMemo(() => {
    const versionFilterValue = searchParams.get('version');
    return graphData.nodes.map((node) => {
      const isGroupNode = node.nodeType === DataObjectRelationNodeType.GROUP;

      let activeVersion: DataObjectVersionType | null = null;

      if (isGroupNode) {
        if (versionFilterValue === 'latest') {
          activeVersion = getGroupNodeActiveVersion(node);
        }
      } else {
        const latestVersion = getLatestDataObjectVersion(
          node?.dataObjectVersions ?? [],
        ) as DataObjectVersionType | null;
        activeVersion = latestVersion ?? null;
      }

      const isInRootGroup =
        graphData.rootNode === node.code ||
        ('dataObjectGroupCode' in node &&
          graphData.rootNode === node.dataObjectGroupCode);

      return {
        id: node.code,
        data: {
          label: `${node.code} - ${node.name}`,
          isInRootGroup,
          isRootNode:
            isInRootGroup &&
            node.outputType !== DataObjectOutputType.INTERMEDIATE,
          dataObjectType: node.dataObjectType,
          dataObjectGroupCode:
            'dataObjectGroupCode' in node
              ? node.dataObjectGroupCode
              : undefined,
          outputType: 'outputType' in node ? node.outputType : undefined,
          domainName: 'domainName' in node ? node.domainName : undefined,
          dataObjectVersions:
            'dataObjectVersions' in node ? node.dataObjectVersions : undefined,
          nodeType: node.nodeType,
          childNodes:
            'childNodes' in node
              ? Array.isArray(node.childNodes)
                ? (node.childNodes as unknown as NodeData[])
                : undefined
              : undefined,
          missingSourceData: node.missingSourceData,
          validationErrorLevelId: node.validationErrorLevelId,
          calculationErrors: node.calculationErrors,
          hasActiveProcessing: node.hasActiveProcessing,
          descriptionId: node?.descriptionId ?? null,
          groupId: node?.groupId ?? null,
          activePeriods: node?.activePeriods ?? null,
          frequency: node?.frequency
            ? {
                ...node.frequency,
                parameters: node.frequency.parameters ?? '',
              }
            : null,
          activeVersion,
          latestValueDate: node.latestValueDate,
          loadingExists: node.loadingExists,
          hasAccess: node.hasAccess,
        },
        position: { x: 0, y: 0 },
        type: 'relationNode',
      };
    });
  }, [graphData, searchParams]);

  const initialEdges: RelationEdgeType[] = useMemo(
    () =>
      graphData.edges.map((edge) => ({
        id: `e${edge.source}->${edge.target}`,
        source: edge.source,
        target: edge.target,
        data: { relationType: edge.relationType },
      })),
    [graphData],
  );

  const [nodes, setNodes, onNodesChange] =
    useNodesState<RelationNodeType>(initialNodes);
  const [edges, setEdges, onEdgesChange] =
    useEdgesState<RelationEdgeType>(initialEdges);
  const [menu, setMenu] = useState<ContextMenuState | null>(null);
  const reactFlowRef = useRef<HTMLDivElement | null>(null);

  const onNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: RelationNodeType) => {
      event.preventDefault();
      const pane = reactFlowRef.current?.getBoundingClientRect();
      if (!pane) return;

      setMenu({
        nodeId: node.id,
        type: node?.data?.dataObjectType ?? DataObjectType.IN,
        left: event.clientX - pane.left,
        top: event.clientY - pane.top,
      });
    },
    [],
  );

  const onPaneClick = useCallback(() => setMenu(null), [setMenu]);

  const setLayout = useCallback(
    (nodesToLayout: RelationNodeType[], edgesToLayout: RelationEdgeType[]) => {
      getLayoutedElements(
        nodesToLayout,
        edgesToLayout,
        graphData.rootNode,
      ).then((result) => {
        if (result) {
          const { nodes: layoutedNodes, edges: layoutedEdges } = result;
          setNodes(layoutedNodes);
          setEdges(
            (layoutedEdges ?? []).map((edge) => ({
              ...edge,
              ...getEdgeStyle(
                edge?.data?.relationType ?? DataObjectRelationEdgeType.ALGO,
              ),
            })),
          );
          setTimeout(() => {
            fitView({ maxZoom: 1, padding: 0.2 });
          }, 100);
        } else {
          console.error('Failed to layout elements.');
        }
      });
    },
    [fitView, setNodes, setEdges, graphData],
  );

  useLayoutEffect(() => {
    setLayout(initialNodes, initialEdges);
  }, [initialNodes, initialEdges, setLayout]);

  return (
    <ReactFlow
      ref={reactFlowRef}
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodeTypes={nodeTypes}
      fitView
      defaultEdgeOptions={{
        focusable: false,
        reconnectable: false,
      }}
      proOptions={{ hideAttribution: true }}
      onPaneClick={onPaneClick}
      onNodeContextMenu={onNodeContextMenu}
    >
      <Controls
        position="top-right"
        showInteractive={false}
        showFitView={false}
      />
      {menu && menu.type === DataObjectType.IN && (
        <InputDataContextMenu {...menu} onClose={() => setMenu(null)} />
      )}
      {menu && menu.type === DataObjectType.OUT && (
        <OutputDataContextMenu
          {...menu}
          refetchGraphData={refetchGraphData}
          onClose={() => setMenu(null)}
        />
      )}
    </ReactFlow>
  );
};

export const RelationsGraph = (props: RelationsGraphProps) => (
  <RelationsGraphCanvas>
    <ReactFlowProvider>
      <RelationsGraphInner {...props} />
    </ReactFlowProvider>
  </RelationsGraphCanvas>
);

const RelationsGraphCanvas = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;
